import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  RefreshCw,
  Send,
  Edit,
  FileText,
  Mail,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";

const STATUS_CONFIG = {
  received: { label: "Received", color: "neutral", icon: Clock },
  processing: { label: "Processing", color: "info", icon: RefreshCw },
  parsed: { label: "Parsed", color: "success", icon: CheckCircle },
  needs_review: { label: "Needs Review", color: "warning", icon: AlertTriangle },
  failed: { label: "Failed", color: "error", icon: XCircle },
  dispatched: { label: "Dispatched", color: "success", icon: CheckCircle },
};

export default function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [parseRuns, setParseRuns] = useState([]);
  const [dsvEnrichments, setDsvEnrichments] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideFields, setOverrideFields] = useState({});
  const [processing, setProcessing] = useState(false);
  const [showRawContent, setShowRawContent] = useState(false);
  const [expandedDispatchLines, setExpandedDispatchLines] = useState(false);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/inbound/items/${itemId}`, {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Item not found");
          navigate("/");
          return;
        }
        throw new Error("Failed to fetch item");
      }

      const data = await response.json();
      setItem(data.item);
      setArtifacts(data.artifacts);
      setParseRuns(data.parse_runs);
      setDsvEnrichments(Array.isArray(data.dsv_enrichments) ? data.dsv_enrichments : []);

      // Determine latest batch (= latest parse generation) and set default selected
      const sortedByDateDesc = [...data.parse_runs].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      const latestBatchId = sortedByDateDesc[0]?.batch_id;
      const latestBatchRuns = latestBatchId
        ? sortedByDateDesc.filter((p) => p.batch_id === latestBatchId)
        : sortedByDateDesc.slice(0, 1);
      const successfulInBatch = latestBatchRuns
        .filter((p) => p.status === "success")
        .sort((a, b) => (a.record_index || 0) - (b.record_index || 0));
      const firstSuccess = successfulInBatch[0] || latestBatchRuns[0];
      if (firstSuccess) {
        setSelectedRunId(firstSuccess.id);
        if (firstSuccess.result_json?.payload) {
          setOverrideFields(firstSuccess.result_json.payload);
        }
      }
    } catch (error) {
      toast.error("Failed to load item");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [itemId, navigate]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  const handleReprocess = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/inbound/items/${itemId}/reprocess`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to reprocess");

      toast.success("Reprocessing started");
      setTimeout(fetchItem, 2000);
    } catch (error) {
      toast.error("Failed to reprocess item");
    } finally {
      setProcessing(false);
    }
  };

  const handleOverride = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/inbound/items/${itemId}/override`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parsed_fields: overrideFields }),
      });

      if (!response.ok) throw new Error("Failed to override");

      toast.success("Override saved");
      setOverrideOpen(false);
      fetchItem();
    } catch (error) {
      toast.error("Failed to save override");
    } finally {
      setProcessing(false);
    }
  };

  const handleDispatch = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/inbound/items/${itemId}/dispatch`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to dispatch");
      }

      const result = await response.json().catch(() => ({}));
      const n = result.records_dispatched || 1;
      toast.success(
        n > 1 ? `Dispatch initiated for ${n} records` : "Dispatch initiated"
      );
      setTimeout(fetchItem, 2000);
    } catch (error) {
      toast.error(error.message || "Failed to dispatch");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.received;
    const Icon = config.icon;

    return (
      <Badge
        variant="outline"
        className={`status-badge-${config.color} flex items-center gap-1`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.7) return "confidence-high";
    if (confidence >= 0.3) return "confidence-medium";
    return "confidence-low";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "PPpp");
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout title="Item Not Found">
        <div className="text-center py-20">
          <p className="text-muted-foreground">Item not found</p>
          <Button onClick={() => navigate("/")} className="mt-4">
            Back to Inbox
          </Button>
        </div>
      </Layout>
    );
  }

  const latestBatchId = parseRuns
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.batch_id;
  const latestBatchRuns = latestBatchId
    ? parseRuns.filter((p) => p.batch_id === latestBatchId)
    : parseRuns.slice(0, 1);
  const dispatchRecords = latestBatchRuns
    .filter((p) => p.status === "success")
    .sort((a, b) => (a.record_index || 0) - (b.record_index || 0));
  const olderRuns = latestBatchId
    ? parseRuns.filter((p) => p.batch_id !== latestBatchId)
    : parseRuns.slice(1);
  const failedInBatch = latestBatchRuns.filter((p) => p.status !== "success");
  const selectedRun =
    parseRuns.find((p) => p.id === selectedRunId) || dispatchRecords[0] || latestBatchRuns[0];
  const canDispatch = dispatchRecords.length > 0;

  return (
    <Layout
      title={
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="truncate max-w-[400px]">
            {item.raw_subject || item.id}
          </span>
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleReprocess}
            disabled={processing}
            data-testid="reprocess-btn"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${processing ? "animate-spin" : ""}`}
            />
            Reprocess
          </Button>
          <Button
            variant="outline"
            onClick={() => setOverrideOpen(true)}
            data-testid="override-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Override
          </Button>
          <Button
            onClick={handleDispatch}
            disabled={processing || !canDispatch}
            data-testid="dispatch-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {dispatchRecords.length > 1
              ? `Dispatch ${dispatchRecords.length} records`
              : "Dispatch"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Item Details & Raw Content */}
        <div className="space-y-6">
          {/* Item Info */}
          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {item.source === "postmark_email" ? (
                  <Mail className="w-4 h-4" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Item Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="overline mb-1">Status</p>
                  {getStatusBadge(item.status)}
                </div>
                <div>
                  <p className="overline mb-1">Target System</p>
                  <Badge variant="outline">{item.target_system}</Badge>
                </div>
                <div>
                  <p className="overline mb-1">Received</p>
                  <p className="text-sm">{formatDate(item.received_at)}</p>
                </div>
                <div>
                  <p className="overline mb-1">Processed</p>
                  <p className="text-sm">
                    {item.processed_at ? formatDate(item.processed_at) : "-"}
                  </p>
                </div>
              </div>

              {/* Document Provider & Type */}
              {(item.document_provider || item.document_type) && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  {item.document_provider && (
                    <div>
                      <p className="overline mb-1">Document Provider</p>
                      <Badge 
                        variant="outline" 
                        className={
                          item.document_provider === "NCB" ? "bg-blue-50 text-blue-700 border-blue-200" :
                          item.document_provider === "SONY" ? "bg-purple-50 text-purple-700 border-purple-200" :
                          "bg-gray-50 text-gray-700 border-gray-200"
                        }
                      >
                        {item.document_provider}
                      </Badge>
                    </div>
                  )}
                  {item.document_type && (
                    <div>
                      <p className="overline mb-1">Document Type</p>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {item.document_type}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Source Provider (Postmark) */}
              {item.provider && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <p className="overline mb-1">Source</p>
                    <Badge variant="secondary">{item.provider}</Badge>
                  </div>
                  {item.message_stream && (
                    <div>
                      <p className="overline mb-1">Stream</p>
                      <p className="text-sm font-mono">{item.message_stream}</p>
                    </div>
                  )}
                </div>
              )}

              {(item.from_email || item.raw_from) && (
                <div>
                  <p className="overline mb-1">From</p>
                  <p className="text-sm">
                    {item.from_name && <span className="font-medium">{item.from_name}</span>}
                    {item.from_name && " "}
                    <span className="text-muted-foreground">
                      {item.from_email || item.raw_from}
                    </span>
                  </p>
                </div>
              )}

              {item.raw_to && (
                <div>
                  <p className="overline mb-1">To</p>
                  <p className="text-sm">{item.raw_to}</p>
                </div>
              )}

              {item.reference_type && (
                <div>
                  <p className="overline mb-1">Reference</p>
                  <p className="text-sm font-mono">
                    {item.reference_type}: {item.reference_id || "-"}
                  </p>
                </div>
              )}

              {item.provider_message_id && (
                <div>
                  <p className="overline mb-1">Message ID</p>
                  <p className="text-sm font-mono truncate">{item.provider_message_id}</p>
                </div>
              )}

              <div>
                <p className="overline mb-1">Item ID</p>
                <p className="text-sm font-mono">{item.id}</p>
              </div>
            </CardContent>
          </Card>

          {/* Artifacts */}
          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Artifacts ({artifacts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {artifacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No attachments</p>
              ) : (
                <div className="space-y-2">
                  {artifacts.map((artifact) => (
                    <div
                      key={artifact.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">
                            {artifact.filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {artifact.content_type} •{" "}
                            {(artifact.size_bytes / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      {artifact.download_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          data-testid={`download-artifact-${artifact.id}`}
                        >
                          <a
                            href={artifact.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Raw Content */}
          {(item.raw_text || item.raw_html) && (
            <Card className="card-surface">
              <CardHeader
                className="cursor-pointer"
                onClick={() => setShowRawContent(!showRawContent)}
              >
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Raw Content</span>
                  {showRawContent ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </CardTitle>
              </CardHeader>
              {showRawContent && (
                <CardContent>
                  <pre className="text-xs font-mono bg-muted p-4 rounded-md overflow-auto max-h-[400px] whitespace-pre-wrap">
                    {item.raw_text || item.raw_html}
                  </pre>
                </CardContent>
              )}
            </Card>
          )}

          {/* DSV / Delivery Tracking (operational debug view for Sony dispatch) */}
          {dsvEnrichments.length > 0 && (
            <DsvTrackingCard
              rows={dsvEnrichments}
              parseRuns={parseRuns}
              itemId={item.id}
              onRowUpdated={(row) => {
                setDsvEnrichments((prev) => {
                  const idx = prev.findIndex(
                    (r) => r.delivery_note === row.delivery_note
                  );
                  if (idx === -1) return prev;
                  const copy = prev.slice();
                  copy[idx] = row;
                  return copy;
                });
              }}
            />
          )}
        </div>

        {/* Right Panel - Parse Results */}
        <div className="space-y-6">
          {/* Multi-record summary banner */}
          {dispatchRecords.length > 1 && (
            <div
              className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
              data-testid="multi-record-banner"
            >
              <strong>{dispatchRecords.length} dispatch records</strong> blev udtrukket fra denne email.
              Alle bliver sendt videre når du klikker Dispatch — vælg en record nedenfor for at gennemse hver enkelt.
            </div>
          )}

          {/* Record selector tabs */}
          {dispatchRecords.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="dispatch-record-tabs">
              {dispatchRecords.map((run) => {
                const total = run.total_records || dispatchRecords.length;
                const idx = run.record_index || dispatchRecords.indexOf(run) + 1;
                const label = total > 1 ? `Dispatch ${idx}/${total}` : "Dispatch";
                const isSelected = run.id === selectedRun?.id;
                const cat = run.result_json?.payload?.catalog_number;
                return (
                  <button
                    key={run.id}
                    onClick={() => {
                      setSelectedRunId(run.id);
                      if (run.result_json?.payload) {
                        setOverrideFields(run.result_json.payload);
                      }
                    }}
                    data-testid={`record-tab-${idx}`}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-white text-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {label}
                    {cat && (
                      <span className={`ml-2 text-xs ${isSelected ? "opacity-80" : "text-muted-foreground"}`}>
                        · {cat}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected record details */}
          {selectedRun && (
            <Card className="card-surface" data-testid="parse-result-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>
                    Parse Result
                    {selectedRun.total_records > 1 && (
                      <span className="ml-2 text-sm text-muted-foreground font-normal">
                        ({selectedRun.record_index}/{selectedRun.total_records})
                      </span>
                    )}
                  </span>
                  <Badge
                    variant="outline"
                    className={`status-badge-${
                      selectedRun.status === "success"
                        ? "success"
                        : selectedRun.status === "needs_review"
                        ? "warning"
                        : "error"
                    }`}
                  >
                    {selectedRun.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Confidence */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline">Confidence</p>
                    <span className="text-sm font-medium">
                      {(selectedRun.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="confidence-bar">
                    <div
                      className={`confidence-bar-fill ${getConfidenceColor(
                        selectedRun.confidence
                      )}`}
                      style={{ width: `${selectedRun.confidence * 100}%` }}
                    />
                  </div>
                </div>

                <Separator />

                {/* Event Type */}
                {selectedRun.result_json?.event_type && (
                  <div>
                    <p className="overline mb-1">Event Type</p>
                    <Badge variant="secondary" className="font-mono">
                      {selectedRun.result_json.event_type}
                    </Badge>
                  </div>
                )}

                {/* Parsed Fields */}
                {selectedRun.result_json?.payload && (
                  <div>
                    <p className="overline mb-2">Extracted Fields</p>
                    <div className="space-y-3">
                      {Object.entries(selectedRun.result_json.payload).map(
                        ([key, value]) => {
                          const isArrayOfObjects =
                            Array.isArray(value) &&
                            value.length > 0 &&
                            value.every(
                              (v) => v && typeof v === "object" && !Array.isArray(v)
                            );
                          if (isArrayOfObjects) {
                            return (
                              <DispatchLinesField
                                key={key}
                                fieldKey={key}
                                rows={value}
                                expanded={expandedDispatchLines}
                                setExpanded={setExpandedDispatchLines}
                              />
                            );
                          }
                          return (
                            <div
                              key={key}
                              className="flex items-start justify-between p-3 bg-muted/50 rounded-md"
                            >
                              <span className="text-sm font-medium capitalize">
                                {key.replace(/_/g, " ")}
                              </span>
                              <span className="text-sm font-mono text-right max-w-[260px] break-words whitespace-pre-wrap">
                                {Array.isArray(value)
                                  ? value.join(", ") || "-"
                                  : value !== null && typeof value === "object"
                                  ? JSON.stringify(value)
                                  : (value?.toString() || "-")}
                              </span>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                {/* Evidence */}
                {Array.isArray(selectedRun.evidence_json) &&
                  selectedRun.evidence_json.length > 0 && (
                    <div>
                      <p className="overline mb-2">Evidence</p>
                      <div className="space-y-2">
                        {selectedRun.evidence_json.map((ev, idx) => (
                          <div
                            key={idx}
                            className="text-xs p-2 bg-blue-50 border border-blue-200 rounded-md"
                          >
                            <span className="font-medium">{ev.field}:</span>{" "}
                            {ev.value && (
                              <span className="font-mono text-blue-700">
                                {String(ev.value)}
                              </span>
                            )}
                            {ev.source && (
                              <span className="text-muted-foreground ml-1">
                                (from {ev.source})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Parser Info */}
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Parser: <span className="font-mono">{selectedRun.parser_id}</span>
                    {" • "}
                    Run ID: <span className="font-mono">{selectedRun.id}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Failed/needs_review records in the latest batch */}
          {failedInBatch.length > 0 && (
            <Card className="card-surface" data-testid="batch-issues-card">
              <CardHeader>
                <CardTitle className="text-base">
                  Records needing attention ({failedInBatch.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {failedInBatch.map((run) => (
                    <div
                      key={run.id}
                      className="flex items-center justify-between p-2 text-sm border-b last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs status-badge-${
                            run.status === "needs_review" ? "warning" : "error"
                          }`}
                        >
                          {run.status}
                        </Badge>
                        <span className="text-muted-foreground font-mono text-xs">
                          {run.parser_id}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {(run.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Parse History — older parse generations only */}
          {olderRuns.length > 0 && (
            <Card className="card-surface" data-testid="parse-history-card">
              <CardHeader>
                <CardTitle className="text-base">Parse History (older runs)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {olderRuns.map((run) => (
                    <div
                      key={run.id}
                      className="flex items-center justify-between p-2 text-sm border-b last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs status-badge-${
                            run.status === "success"
                              ? "success"
                              : run.status === "needs_review"
                              ? "warning"
                              : "error"
                          }`}
                        >
                          {run.status}
                        </Badge>
                        <span className="text-muted-foreground font-mono text-xs">
                          {run.parser_id}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {(run.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Parse Results */}
          {parseRuns.length === 0 && (
            <Card className="card-surface">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No parse results yet</p>
                <Button
                  variant="outline"
                  onClick={handleReprocess}
                  className="mt-4"
                >
                  Run Parsers
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Override Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Override Parsed Fields</DialogTitle>
            <DialogDescription>
              Manually correct the extracted fields. This will set the item
              status to Parsed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedRun?.result_json?.payload ? (
              Object.entries(selectedRun.result_json.payload).map(
                ([key, value]) => (
                  <div key={key} className="space-y-2">
                    <Label className="capitalize">{key.replace(/_/g, " ")}</Label>
                    <Input
                      value={overrideFields[key] || ""}
                      onChange={(e) =>
                        setOverrideFields((f) => ({
                          ...f,
                          [key]: e.target.value,
                        }))
                      }
                      placeholder={value?.toString() || ""}
                      data-testid={`override-field-${key}`}
                    />
                  </div>
                )
              )
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Input
                    value={overrideFields.event_type || ""}
                    onChange={(e) =>
                      setOverrideFields((f) => ({
                        ...f,
                        event_type: e.target.value,
                      }))
                    }
                    placeholder="NCB_CLEARANCE_APPROVED"
                    data-testid="override-field-event_type"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catalog Number</Label>
                  <Input
                    value={overrideFields.catalog_no || ""}
                    onChange={(e) =>
                      setOverrideFields((f) => ({
                        ...f,
                        catalog_no: e.target.value,
                      }))
                    }
                    placeholder="AERO015"
                    data-testid="override-field-catalog_no"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Input
                    value={overrideFields.status || ""}
                    onChange={(e) =>
                      setOverrideFields((f) => ({
                        ...f,
                        status: e.target.value,
                      }))
                    }
                    placeholder="approved"
                    data-testid="override-field-status"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleOverride} disabled={processing}>
              Save Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// DispatchLinesField — renders array-of-objects (e.g. Sony split delivery
// dispatch_lines[]) as a compact table with optional raw-JSON drill-down.
// Prevents the old "[object Object],[object Object]" rendering.
// ---------------------------------------------------------------------------
function DispatchLinesField({ fieldKey, rows, expanded, setExpanded }) {
  const preferredOrder = [
    "delivery_note",
    "quantity",
    "pallet",
    "arrival_date",
    "planned_arrival_date",
    "catalog_number",
    "line_ref",
  ];
  const allKeys = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  const orderedKeys = [
    ...preferredOrder.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !preferredOrder.includes(k)),
  ];

  const renderCell = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    if (Array.isArray(v)) return v.join(", ") || "-";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <div
      className="p-3 bg-muted/50 rounded-md"
      data-testid={`dispatch-lines-${fieldKey}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium capitalize">
          {fieldKey.replace(/_/g, " ")}{" "}
          <span className="text-xs text-muted-foreground font-normal">
            ({rows.length})
          </span>
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid={`dispatch-lines-toggle-${fieldKey}`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" /> Hide JSON
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" /> Show JSON
            </>
          )}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              {orderedKeys.map((k) => (
                <th key={k} className="py-1 pr-3 font-medium capitalize">
                  {k.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b last:border-b-0">
                {orderedKeys.map((k) => (
                  <td
                    key={k}
                    className="py-1 pr-3 align-top break-all"
                  >
                    {renderCell(row?.[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {expanded && (
        <pre className="mt-2 text-xs bg-background border rounded p-2 overflow-auto max-h-[240px]">
          {JSON.stringify(rows, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DsvTrackingCard — operational/debug view of the dsv_enrichments rows that
// back this InboundItem's Delivery Notes. Reuses the projection returned by
// GET /api/inbound/items/{id} — no new tracking model.
// ---------------------------------------------------------------------------
function DsvTrackingCard({ rows, parseRuns, itemId, onRowUpdated }) {
  const _prettifyStatus = (norm) => {
    if (!norm) return norm;
    return norm
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };
  const [checkingDn, setCheckingDn] = useState(null);
  const [checkError, setCheckError] = useState({}); // dn -> error string
  const [markingDn, setMarkingDn] = useState(null); // dn being mark/unmark'd
  const [carrierPromptDn, setCarrierPromptDn] = useState(null); // dn awaiting carrier input
  const [carrierDraft, setCarrierDraft] = useState("");

  const runCheckNow = async (dn) => {
    if (checkingDn) return; // prevent concurrent clicks across rows
    setCheckingDn(dn);
    setCheckError((prev) => ({ ...prev, [dn]: null }));
    try {
      const url = `${API}/inbound/items/${itemId}/tracking/${encodeURIComponent(dn)}/check-now`;
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setCheckError((prev) => ({
          ...prev,
          [dn]: body?.detail || `HTTP ${resp.status}`,
        }));
        toast.error(`Check failed for DN ${dn}: ${body?.detail || resp.status}`);
        return;
      }
      if (body?.error) {
        setCheckError((prev) => ({ ...prev, [dn]: body.error }));
        toast.error(`DN ${dn}: ${body.error}`);
      } else {
        const parts = [];
        if (body.resolved) parts.push("resolved");
        if (body.polled) parts.push("polled");
        if (body.pushed_reason) parts.push(`FLOW ${body.pushed_reason}`);
        toast.success(`DN ${dn} checked${parts.length ? ` — ${parts.join(", ")}` : ""}`);
      }
      if (body?.row && typeof onRowUpdated === "function") {
        onRowUpdated(body.row);
      }
    } catch (e) {
      setCheckError((prev) => ({ ...prev, [dn]: String(e) }));
      toast.error(`Check failed for DN ${dn}: ${e}`);
    } finally {
      setCheckingDn(null);
    }
  };

  // Mark DN as not shipped via DSV. Carrier text is optional but encouraged.
  const runMarkNonDsv = async (dn, carrier) => {
    if (markingDn || checkingDn) return;
    setMarkingDn(dn);
    setCheckError((prev) => ({ ...prev, [dn]: null }));
    try {
      const url = `${API}/inbound/items/${itemId}/tracking/${encodeURIComponent(
        dn
      )}/mark-non-dsv`;
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carrier: (carrier || "").trim() || null }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setCheckError((prev) => ({ ...prev, [dn]: body?.detail || `HTTP ${resp.status}` }));
        toast.error(`Mark non-DSV failed: ${body?.detail || resp.status}`);
        return;
      }
      toast.success(
        `DN ${dn} marked as non-DSV${body.non_dsv_carrier ? ` (${body.non_dsv_carrier})` : ""}`
      );
      // Reflect in-place: the row's status is enough; carrier shows in badge.
      if (typeof onRowUpdated === "function") {
        onRowUpdated({
          delivery_note: dn,
          status: body.status,
          non_dsv_carrier: body.non_dsv_carrier || null,
          non_dsv_marked_at: body.non_dsv_marked_at || null,
          attempts: 0,
          next_attempt_at: null,
          last_error: null,
          tracking: null,
          flow_push: null,
        });
      }
      setCarrierPromptDn(null);
      setCarrierDraft("");
    } catch (e) {
      setCheckError((prev) => ({ ...prev, [dn]: String(e) }));
      toast.error(`Mark non-DSV failed: ${e}`);
    } finally {
      setMarkingDn(null);
    }
  };

  const runUnmarkNonDsv = async (dn) => {
    if (markingDn || checkingDn) return;
    setMarkingDn(dn);
    try {
      const url = `${API}/inbound/items/${itemId}/tracking/${encodeURIComponent(
        dn
      )}/unmark-non-dsv`;
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(`Unmark failed: ${body?.detail || resp.status}`);
        return;
      }
      toast.success(`DN ${dn} re-enabled for DSV lookup`);
      if (typeof onRowUpdated === "function") {
        onRowUpdated({
          delivery_note: dn,
          status: body.status,
          non_dsv_carrier: null,
          non_dsv_marked_at: null,
          attempts: 0,
          last_error: null,
          next_attempt_at: null,
          tracking: null,
          flow_push: null,
        });
      }
    } catch (e) {
      toast.error(`Unmark failed: ${e}`);
    } finally {
      setMarkingDn(null);
    }
  };

  const fmtAbs = (iso) => {
    if (!iso) return "-";
    try {
      return format(new Date(iso), "PP p");
    } catch {
      return iso;
    }
  };
  const fmtRel = (iso) => {
    if (!iso) return "-";
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
      return iso;
    }
  };

  // Lifecycle projection per row.
  const lifecycleOf = (row) => {
    const status = row?.status;
    const trk = row?.tracking || {};
    const trkStatus = trk?.status;
    const normalized = trk?.dsv_status_normalized;
    if (status === "non_dsv") {
      const carrier = row?.non_dsv_carrier || "Other carrier";
      return { key: "non_dsv", label: `Not shipped via DSV · ${carrier}`, tone: "muted" };
    }
    if (status === "permanent_failure" || status === "exhausted") {
      return { key: "problem", label: "Tracking problem", tone: "error" };
    }
    if (trkStatus === "delivered" || normalized === "delivered") {
      return { key: "delivered", label: "Delivered", tone: "success" };
    }
    if (status === "resolved" && trkStatus === "active") {
      return { key: "active", label: "Tracking active", tone: "info" };
    }
    if (trkStatus === "exhausted") {
      return { key: "problem", label: "Tracking problem", tone: "error" };
    }
    return { key: "waiting", label: "Waiting for DSV", tone: "warning" };
  };

  // FLOW push projection per row.
  const flowStateOf = (row) => {
    const fp = row?.flow_push || {};
    if (fp.last_error) {
      return {
        key: "failed",
        label: "FLOW push failed",
        detail: fp.last_error,
        tone: "error",
      };
    }
    if (fp.last_pushed_at && (fp.last_status_code == null || fp.last_status_code === 200)) {
      return {
        key: "updated",
        label: "FLOW updated",
        detail: fmtAbs(fp.last_pushed_at),
        tone: "success",
      };
    }
    return { key: "pending", label: "FLOW pending", detail: null, tone: "warning" };
  };

  // Pipeline aggregation — top-of-card indicator. Reflects the DSV tracking
  // lifecycle specifically: `FLOW updated` here means at least one successful
  // *tracking snapshot* push landed on Flow (not the original/reparse dispatch,
  // which is intentionally excluded since `flow_push.last_pushed_at` is only
  // written by `mark_flow_pushed` after a `tracking_changed` webhook).
  const anyResolved = rows.some((r) => r?.stt_number);
  const anyActive = rows.some(
    (r) => (r?.tracking?.status === "active") || (r?.tracking?.status === "delivered")
  );
  const anyFlowUpdated = rows.some(
    (r) =>
      r?.flow_push?.last_pushed_at &&
      !r?.flow_push?.last_error &&
      (r?.flow_push?.last_status_code == null || r?.flow_push?.last_status_code === 200)
  );
  const anySonyDispatchParsed = parseRuns?.some?.(
    (pr) => pr?.status === "success" && pr?.parser_id?.toLowerCase?.().includes("sony")
  ) || parseRuns?.length > 0;

  const steps = [
    { key: "parsed", label: "Sony parsed", done: !!anySonyDispatchParsed },
    { key: "found", label: "DSV found", done: anyResolved },
    { key: "active", label: "Tracking active", done: anyActive },
    { key: "flow", label: "FLOW updated", done: anyFlowUpdated },
  ];

  const toneClass = (tone) =>
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : tone === "error"
      ? "bg-red-50 text-red-700 border-red-200"
      : tone === "info"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : tone === "warning"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-muted text-muted-foreground";

  return (
    <Card className="card-surface" data-testid="dsv-tracking-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Delivery Tracking</span>
          <span className="text-xs text-muted-foreground font-normal">
            {rows.length} {rows.length === 1 ? "delivery note" : "delivery notes"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pipeline indicator */}
        <div
          className="flex items-center flex-wrap gap-2 text-xs"
          data-testid="dsv-pipeline"
        >
          {steps.map((s, idx) => (
            <React.Fragment key={s.key}>
              <span
                className={`px-2 py-1 rounded border ${
                  s.done
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-muted text-muted-foreground border-border"
                }`}
                data-testid={`dsv-step-${s.key}`}
              >
                {s.done ? "✓ " : "○ "}
                {s.label}
              </span>
              {idx < steps.length - 1 && (
                <span className="text-muted-foreground">→</span>
              )}
            </React.Fragment>
          ))}
        </div>

        <Separator />

        {/* Per Delivery Note rows */}
        <div className="space-y-3">
          {rows.map((row) => {
            const lc = lifecycleOf(row);
            const fp = flowStateOf(row);
            const trk = row?.tracking || {};
            const ev = trk?.latest_event || null;
            const waiting = lc.key === "waiting";
            const dsvEta = trk?.delivered_at || trk?.estimated_delivery_date;
            return (
              <div
                key={row.delivery_note}
                className="border rounded-md p-3 space-y-2"
                data-testid={`dsv-row-${row.delivery_note}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium">
                      DN {row.delivery_note}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${toneClass(lc.tone)}`}
                      data-testid={`dsv-lifecycle-${row.delivery_note}`}
                    >
                      {lc.label}
                    </Badge>
                    {trk?.dsv_status_normalized && (
                      <Badge variant="secondary" className="text-xs font-mono">
                        {trk.dsv_status_normalized}
                      </Badge>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs ${toneClass(fp.tone)}`}
                    data-testid={`dsv-flow-${row.delivery_note}`}
                    title={fp.detail || ""}
                  >
                    {fp.label}
                    {fp.detail && fp.key === "updated" && (
                      <span className="ml-1 opacity-80">· {fp.detail}</span>
                    )}
                  </Badge>
                  {row.status === "non_dsv" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() => runUnmarkNonDsv(row.delivery_note)}
                      disabled={!!markingDn || !!checkingDn}
                      data-testid={`dsv-unmark-non-dsv-${row.delivery_note}`}
                    >
                      {markingDn === row.delivery_note ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Reverting…
                        </>
                      ) : (
                        <>Resume DSV tracking</>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => runCheckNow(row.delivery_note)}
                        disabled={!!checkingDn || !!markingDn}
                        data-testid={`dsv-check-now-${row.delivery_note}`}
                      >
                        {checkingDn === row.delivery_note ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Checking…
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Check now
                          </>
                        )}
                      </Button>
                      {waiting && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setCarrierPromptDn(
                              carrierPromptDn === row.delivery_note
                                ? null
                                : row.delivery_note
                            );
                            setCarrierDraft("");
                          }}
                          disabled={!!markingDn || !!checkingDn}
                          data-testid={`dsv-mark-non-dsv-${row.delivery_note}`}
                        >
                          Not DSV?
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {carrierPromptDn === row.delivery_note && (
                  <div className="bg-muted/50 border rounded p-2 space-y-2 text-xs">
                    <p>
                      Mark this delivery note as shipped via another carrier?
                      Cron will stop retrying DSV lookups for it.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {["UPS", "FedEx", "DHL", "GLS", "PostNord"].map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`px-2 py-1 rounded border text-xs ${
                            carrierDraft === c
                              ? "bg-foreground text-background"
                              : "bg-background hover:bg-muted"
                          }`}
                          onClick={() => setCarrierDraft(c)}
                          data-testid={`dsv-carrier-chip-${c}`}
                        >
                          {c}
                        </button>
                      ))}
                      <input
                        type="text"
                        value={carrierDraft}
                        onChange={(e) => setCarrierDraft(e.target.value)}
                        placeholder="Other…"
                        className="flex-1 min-w-[120px] px-2 py-1 rounded border text-xs"
                        data-testid={`dsv-carrier-input-${row.delivery_note}`}
                        maxLength={80}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() =>
                          runMarkNonDsv(row.delivery_note, carrierDraft)
                        }
                        disabled={!!markingDn}
                        data-testid={`dsv-mark-non-dsv-confirm-${row.delivery_note}`}
                      >
                        {markingDn === row.delivery_note ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Saving…
                          </>
                        ) : (
                          <>Mark as non-DSV</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          setCarrierPromptDn(null);
                          setCarrierDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {row.status === "non_dsv" ? (
                  <div className="text-xs text-muted-foreground bg-muted/50 border rounded p-2 space-y-0.5">
                    <div>
                      Shipped via{" "}
                      <span className="font-medium text-foreground">
                        {row.non_dsv_carrier || "another carrier"}
                      </span>{" "}
                      — DSV tracking disabled.
                    </div>
                    {row.non_dsv_marked_at && (
                      <div className="font-mono opacity-80">
                        Marked {fmtRel(row.non_dsv_marked_at)}
                      </div>
                    )}
                    {row.non_dsv_note && (
                      <div className="opacity-80 italic">
                        “{row.non_dsv_note}”
                      </div>
                    )}
                  </div>
                ) : waiting ? (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
                    <div>Waiting for DSV tracking</div>
                    <div className="font-mono opacity-80">
                      {row.last_attempted_at && (
                        <>Last check: {fmtRel(row.last_attempted_at)}</>
                      )}
                      {row.last_attempted_at && row.next_attempt_at && " · "}
                      {row.next_attempt_at && (
                        <>Next check: {fmtRel(row.next_attempt_at)}</>
                      )}
                    </div>
                    {row.last_error && (
                      <div className="opacity-80">
                        Last error: {row.last_error}
                        {row.attempts != null && <> (attempt {row.attempts})</>}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Prominent current status when known */}
                    {trk?.dsv_status_normalized && (
                      <div
                        className="text-sm bg-blue-50 border border-blue-200 rounded p-2"
                        data-testid={`dsv-current-status-${row.delivery_note}`}
                      >
                        <p className="overline mb-0.5 text-blue-700">Current DSV status</p>
                        <p className="font-medium text-blue-900">
                          {_prettifyStatus(trk.dsv_status_normalized)}
                        </p>
                      </div>
                    )}

                    {/* Progressive disclosure — only show cells that have data */}
                    {(() => {
                      const cells = [];
                      if (row.sony_original_planned_arrival_date) {
                        cells.push({
                          label: "Sony planned",
                          value: row.sony_original_planned_arrival_date,
                          testid: "dsv-sony-planned",
                        });
                      }
                      if (dsvEta) {
                        cells.push({
                          label: trk?.delivered_at ? "Delivered" : "DSV ETA",
                          value: dsvEta,
                          testid: "dsv-eta",
                        });
                      }
                      if (trk?.last_checked_at) {
                        cells.push({
                          label: "Last checked",
                          value: fmtRel(trk.last_checked_at),
                          testid: "dsv-last-checked",
                        });
                      }
                      if (trk?.next_check_at) {
                        cells.push({
                          label: "Next check",
                          value: fmtRel(trk.next_check_at),
                          testid: "dsv-next-check",
                        });
                      }
                      if (trk?.last_changed_at) {
                        cells.push({
                          label: "Last changed",
                          value: fmtRel(trk.last_changed_at),
                          testid: "dsv-last-changed",
                        });
                      }
                      if (cells.length === 0) return null;
                      return (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {cells.map((c) => (
                            <div key={c.label}>
                              <p className="overline mb-0.5">{c.label}</p>
                              <p
                                className="font-mono"
                                data-testid={`${c.testid}-${row.delivery_note}`}
                              >
                                {c.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Latest event — only when populated */}
                    {ev && ev.description && (
                      <div
                        className="text-xs"
                        data-testid={`dsv-latest-event-${row.delivery_note}`}
                      >
                        <p className="overline mb-0.5">Latest event</p>
                        <p>
                          {ev.description}
                          {ev.occurred_at && (
                            <span className="text-muted-foreground ml-2">
                              · {fmtAbs(ev.occurred_at)}
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    {/* STT — internal admin/debug, secondary */}
                    {row.stt_number && (
                      <div className="text-xs text-muted-foreground">
                        <span className="opacity-70">STT (internal):</span>{" "}
                        <span
                          className="font-mono"
                          data-testid={`dsv-stt-${row.delivery_note}`}
                        >
                          {row.stt_number}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {fp.key === "failed" && fp.detail && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 font-mono break-all">
                    {fp.detail}
                  </div>
                )}
                {checkError[row.delivery_note] && (
                  <div
                    className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 font-mono break-all"
                    data-testid={`dsv-check-error-${row.delivery_note}`}
                  >
                    Check error: {checkError[row.delivery_note]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

